import { Autocomplete, Box, TextField } from "@mui/material";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import {
  StudentClassOption,
  StudentClassOptionsArgs,
  useStudentClassOptions,
} from "./useStudentClassOptions";

export interface StudentClassSelectProps
  extends Omit<StudentClassOptionsArgs, "term"> {
  label: string;
  value: StudentClassOption | null;
  onChange: (value: StudentClassOption | null) => void;
}

/** One class, searched as the viewer types (300 ms debounce): for bulk enroll and Move. */
export const StudentClassSelect = ({
  label,
  value,
  onChange,
  ...args
}: StudentClassSelectProps) => {
  const { t } = useTranslation();
  const [input, setInput] = useState("");
  const [term, setTerm] = useState("");
  useEffect(() => {
    const timer = setTimeout(() => setTerm(input.trim()), 300);
    return () => clearTimeout(timer);
  }, [input]);
  // While a class is picked the input shows its name; search from an empty term then.
  const searchTerm = value && input === value.name ? "" : term;
  const { data, isLoading, isFetching } = useStudentClassOptions({
    ...args,
    term: searchTerm,
  });

  return (
    <Autocomplete<StudentClassOption>
      options={data ?? []}
      value={value}
      onChange={(_event, next) => onChange(next)}
      inputValue={input}
      onInputChange={(_event, next) => setInput(next)}
      filterOptions={(options) => options}
      getOptionLabel={(option) => option.name}
      isOptionEqualToValue={(option, picked) => option.id === picked.id}
      loading={Boolean(isLoading || isFetching)}
      loadingText={t("common.loading")}
      noOptionsText={t("people.students.classPicker.noClasses")}
      renderOption={(props, option) => {
        const { key: _key, ...rest } = props as typeof props & { key?: string };
        return (
          <Box component="li" {...rest} key={option.id}>
            <Box sx={{ display: "flex", flexDirection: "column", minWidth: 0 }}>
              <span>{option.name}</span>
              {option.schoolName ? (
                <Box
                  component="span"
                  sx={{ fontSize: 13, color: "text.secondary" }}
                >
                  {option.schoolName}
                </Box>
              ) : null}
            </Box>
          </Box>
        );
      }}
      renderInput={(params) => (
        <TextField {...params} size="small" label={label} />
      )}
    />
  );
};

export default StudentClassSelect;
